import type { RouteSettingsEntry } from './types';

function isUsable(value: unknown): boolean {
  return value !== undefined && value !== null && value !== '';
}

/**
 * Substitute route parameters into a path template. Resolution order is mirrored
 * by `substitute_route_params()` in `app/core/src/helpers.php`:
 *
 *   1. Key match — a param whose key matches a placeholder wins.
 *   2. Alias trail — a renamed param resolves through the `old key => current key`
 *      map, so stored links authored before an id-tracked rename keep working.
 *   3. Guarded positional fallback — when a single placeholder and a single unused
 *      value remain, fill it. This is the only rescue for renames that predate
 *      rename tracking (no id, so no alias), which stored links still address by
 *      the old key; restricting it to the unambiguous single-param case keeps it
 *      from ever resolving a multi-param URL to the wrong record.
 *
 * A placeholder left unfilled by all three is returned as-is for the caller to reject.
 */
export function interpolateRouteParams(
  route: string,
  params: Record<string, unknown>,
  aliases: Record<string, string> = {}
): string {
  const consumed = new Set<string>();
  const keyMatchedButEmpty = new Set<string>();

  const byKey = route.replace(/\{([^}]+)\}/g, (match, paramName) => {
    if (!(paramName in params)) {
      return match;
    }

    consumed.add(paramName);
    const value = params[paramName];

    if (!isUsable(value)) {
      keyMatchedButEmpty.add(paramName);

      return match;
    }

    return encodeURIComponent(value as string);
  });

  // Iterated in `params` order to match the backend: when two aliases target one
  // placeholder, both sides must pick the same winner.
  let byAlias = byKey;

  for (const [key, value] of Object.entries(params)) {
    if (consumed.has(key) || !isUsable(value)) {
      continue;
    }

    const target = aliases[key];

    if (target === undefined || target === '' || keyMatchedButEmpty.has(target)) {
      continue;
    }

    const placeholder = `{${target}}`;

    if (!byAlias.includes(placeholder)) {
      continue;
    }

    consumed.add(key);
    byAlias = byAlias.split(placeholder).join(encodeURIComponent(value as string));
  }

  const unresolved = [...byAlias.matchAll(/\{([^}]+)\}/g)];

  if (unresolved.length === 1 && !keyMatchedButEmpty.has(unresolved[0][1])) {
    const leftover = Object.entries(params).filter(
      ([key, value]) => !consumed.has(key) && isUsable(value)
    );

    if (leftover.length === 1) {
      const [key, value] = leftover[0];
      consumed.add(key);
      byAlias = byAlias.split(unresolved[0][0]).join(encodeURIComponent(value as string));
    }
  }

  return byAlias;
}

export function buildUrlFromSettings(
  entry: RouteSettingsEntry | undefined,
  entityLabel: 'Workflow' | 'Page',
  id: string,
  params: Record<string, unknown>
): string {
  if (!entry?.route) {
    throw new Error(`${entityLabel} '${id}' does not have a published route.`);
  }

  const url = interpolateRouteParams(entry.route, params, entry.route_param_aliases ?? {});

  const unresolved = url.match(/\{([^}]+)\}/);
  if (unresolved) {
    throw new Error(`The '${unresolved[1]}' parameter is required but was not provided.`);
  }

  return url;
}

export function resolveUrlTarget(
  target: string | { id: string; params?: Record<string, unknown> } | null | undefined,
  paramsArg: Record<string, unknown> | undefined,
  missingIdMessage: string
): { id: string; params: Record<string, unknown> } {
  const id = typeof target === 'string' ? target : target?.id;
  const params = typeof target === 'string' ? paramsArg : (target?.params ?? paramsArg);

  if (!id) {
    throw new Error(missingIdMessage);
  }

  return { id, params: params ?? {} };
}
